import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { showBanner } from '../../global-state/banner-slice'
import { showModel } from '../../global-state/confirm-model-slice'
import { RootState } from '../../store'
import {
  campaignServices,
  GetPromotionalReferralCodesParams,
  normalizeCampaignListResponse,
  PromotionalReferralCodeListItem,
  CampaignListPagination,
} from '../../services/campaign-service'
import { CampaignsListPresenter } from './campaigns-list-presenter'

const DEFAULT_LIMIT = 10
const DEFAULT_SEARCH_PARAMS: GetPromotionalReferralCodesParams = {
  limit: DEFAULT_LIMIT.toString(),
  offset: '0',
  keyword: '',
  sortBy: 'NEWEST_TO_OLDEST',
}
const DEFAULT_PAGINATION: CampaignListPagination = {
  total: 0,
  limit: DEFAULT_LIMIT,
  count: 0,
  offset: 0,
}

function CampaignsListContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isConfirm } = useSelector((state: RootState) => state.confirmModel)
  const [campaigns, setCampaigns] = useState<PromotionalReferralCodeListItem[]>([])
  const [searchParams, setSearchParams] =
    useState<GetPromotionalReferralCodesParams>(DEFAULT_SEARCH_PARAMS)
  const [pagination, setPagination] = useState<CampaignListPagination>(DEFAULT_PAGINATION)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [deleteId, setDeleteId] = useState<string | number | null>(null)
  const [listRefreshKey, setListRefreshKey] = useState(0)

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true)
      const res = await campaignServices.listPromotionalReferralCodes(searchParams)
      if (res?.status === 200) {
        const { items, pagination: pg } = normalizeCampaignListResponse(res.data)
        setCampaigns(items)
        setPagination(pg)
      } else {
        setCampaigns([])
        setPagination(DEFAULT_PAGINATION)
        const message =
          typeof res?.data?.message === 'string' && res.data.message.trim()
            ? res.data.message
            : res?.status
            ? `Could not load campaigns (${res.status}).`
            : 'Could not load campaigns.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
      setIsLoading(false)
    }
    fetchCampaigns()
  }, [currentPage, searchParams, dispatch, listRefreshKey])

  useEffect(() => {
    if (!isConfirm || deleteId === null) return

    const id = deleteId
    setDeleteId(null)

    const runDelete = async () => {
      const res = await campaignServices.deletePromotionalReferralCode(id)
      if (res?.status === 204) {
        setListRefreshKey((k) => k + 1)
        dispatch(showBanner({ text: 'Campaign deleted successfully', variant: 'success-banner' }))
      } else {
        dispatch(
          showBanner({
            text:
              typeof res?.data?.message === 'string' && res.data.message.trim()
                ? res.data.message
                : res?.status
                ? `Could not delete campaign (${res.status}).`
                : 'Could not delete campaign.',
            variant: 'error-banner',
          }),
        )
      }
    }
    void runDelete()
  }, [isConfirm, deleteId, dispatch])

  const handleEditCampaign = (row: PromotionalReferralCodeListItem) => {
    navigate(`/campaigns/${row.id}/edit`, { state: { campaign: row } })
  }

  const handleDeleteCampaign = (row: PromotionalReferralCodeListItem) => {
    dispatch(
      showModel({
        name: 'Delete campaign',
        message: `Delete campaign "${row.code}"? This cannot be undone.`,
      }),
    )
    setDeleteId(row.id)
  }

  const handleSearchParams = ({ name, value }: { name: string; value: string }) => {
    const { limit } = DEFAULT_PAGINATION
    setSearchParams((prev) => ({
      ...prev,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    }))
    setCurrentPage(1)
    setPagination(DEFAULT_PAGINATION)
  }

  const resetSearchParams = () => {
    setSearchParams(DEFAULT_SEARCH_PARAMS)
    setCurrentPage(1)
    setPagination(DEFAULT_PAGINATION)
  }

  const handleCurrentPage = (page: number) => {
    const { limit } = DEFAULT_PAGINATION
    setCurrentPage(page)
    setSearchParams((prev) => ({
      ...prev,
      offset: `${(page - 1) * limit}`,
    }))
    setPagination((prev) => ({ ...prev, offset: (page - 1) * limit }))
  }

  return (
    <CampaignsListPresenter
      campaigns={campaigns}
      isLoading={isLoading}
      pagination={pagination}
      currentPage={currentPage}
      searchParams={searchParams}
      handleCurrentPage={handleCurrentPage}
      handleSearchParams={handleSearchParams}
      resetSearchParams={resetSearchParams}
      onEditCampaign={handleEditCampaign}
      onDeleteCampaign={handleDeleteCampaign}
    />
  )
}

export { CampaignsListContainer }
